import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/storage-locations`;

/**
 * Storage Location Service
 * Gerencia todas as operações de locais de armazenamento
 */
class StorageLocationService {
  /**
   * Todos os locais da empresa, do mais recente ao mais antigo (a API monta
   * a lista com HashSet, sem ordem garantida). Empresa sem locais recebe 404
   * da API: aqui vira [] sem abrir o modal global.
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array>}
   */
  async getAllStorageLocations(options = {}) {
    try {
      const response = await api.get(API_BASE, { silentStatuses: [404], ...options });
      return unwrapList(response).sort((a, b) => Number(b.id) - Number(a.id));
    } catch (error) {
      if (error.response?.status === 404) return [];
      console.error("❌ Erro ao buscar locais de armazenamento:", error);
      throw error;
    }
  }

  /**
   * Get storage location by ID
   * @param {number} id - Storage Location ID
   * @returns {Promise<object>}
   */
  async getStorageLocationById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar local de armazenamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new storage location
   * @param {object} locationData - Storage location data
   * @returns {Promise<object>}
   */
  async createStorageLocation(locationData) {
    try {
      // Validar dados antes de enviar
      this.validateStorageLocationData(locationData);

      const response = await api.post(
        API_BASE,
        locationData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar local de armazenamento:", error);
      throw error;
    }
  }

  /**
   * Update storage location
   * @param {number} id - Storage Location ID
   * @param {object} locationData - Updated storage location data
   * @returns {Promise<object>}
   */
  async updateStorageLocation(id, locationData) {
    try {
      // Validar dados antes de enviar
      this.validateStorageLocationData(locationData);

      const response = await api.put(
        `${API_BASE}/${id}`,
        locationData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(
        `❌ Erro ao atualizar local de armazenamento ${id}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete storage location
   * @param {number} id - Storage Location ID
   * @returns {Promise<void>}
   */
  async deleteStorageLocation(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar local de armazenamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate storage location data
   * @param {object} data - Storage location data to validate
   * @throws {Error} If validation fails
   */
  validateStorageLocationData(data) {
    const errors = [];

    const name = (data.name || "").trim();
    if (name.length < 3 || name.length > 100) {
      errors.push("O nome deve ter entre 3 e 100 caracteres");
    }

    if (!data.equipment_id) {
      errors.push("Equipamento é obrigatório");
    }

    if (!data.capacity || data.capacity <= 0) {
      errors.push("Capacidade deve ser maior que zero");
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }
}

export default new StorageLocationService();
